import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "./AuthContext";
import { fetchSubscriptionValidityForCurrentUser } from "../utils/subscriptionValidityApi";
import {
  canAccessPathWithSubscription,
  getSubscriptionRedirectForPath,
  isRestrictedMenuDisabled,
  isSubscriptionRestrictedPath,
  STATUS_ERROR,
  STATUS_NOT_PURCHASED
} from "../utils/subscriptionValidityUtils";

const SubscriptionAccessContext = createContext({
  status: null,
  checking: false,
  expirationDate: null,
  validityError: "",
  showSubscribeModal: false,
  dismissSubscribeModal: () => {},
  openSubscribeModal: () => {},
  refreshSubscriptionValidity: async () => null,
  navigateWithSubscriptionCheck: async () => false,
  isRestrictedMenuDisabled: () => false,
  canAccessPath: () => true
});

const SubscriptionAccessProvider = ({ children }) => {
  const { user, loggedIn } = useAuthContext();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [expirationDate, setExpirationDate] = useState(null);
  const [validityError, setValidityError] = useState("");
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const requestIdRef = useRef(0);
  const hasCheckedOnLoginRef = useRef(false);

  const applyResult = useCallback((parsed) => {
    const nextStatus = parsed?.status ?? STATUS_ERROR;
    setStatus(nextStatus);
    setExpirationDate(parsed?.expirationDate || null);
    setValidityError(nextStatus === STATUS_ERROR ? parsed?.message || "" : "");
    return {
      status: nextStatus,
      expirationDate: parsed?.expirationDate || null,
      message: parsed?.message || null
    };
  }, []);

  const refreshSubscriptionValidity = useCallback(async () => {
    if (!loggedIn || !user) {
      setStatus(null);
      setExpirationDate(null);
      setValidityError("");
      setShowSubscribeModal(false);
      return null;
    }

    const requestId = ++requestIdRef.current;
    setChecking(true);
    try {
      const parsed = await fetchSubscriptionValidityForCurrentUser();
      if (requestId !== requestIdRef.current) {
        return parsed;
      }
      return applyResult(parsed);
    } finally {
      if (requestId === requestIdRef.current) {
        setChecking(false);
      }
    }
  }, [loggedIn, user, applyResult]);

  useEffect(() => {
    if (!loggedIn || !user) {
      hasCheckedOnLoginRef.current = false;
      setStatus(null);
      setExpirationDate(null);
      setValidityError("");
      setShowSubscribeModal(false);
      return undefined;
    }

    let cancelled = false;

    const runInitialCheck = async () => {
      const result = await refreshSubscriptionValidity();
      if (cancelled || !result) return;
      if (!hasCheckedOnLoginRef.current && result.status === STATUS_NOT_PURCHASED) {
        setShowSubscribeModal(true);
      }
      hasCheckedOnLoginRef.current = true;
    };

    runInitialCheck();
    return () => {
      cancelled = true;
    };
  }, [loggedIn, user, refreshSubscriptionValidity]);

  const dismissSubscribeModal = useCallback(() => {
    setShowSubscribeModal(false);
  }, []);

  const openSubscribeModal = useCallback(() => {
    setShowSubscribeModal(true);
  }, []);

  const navigateWithSubscriptionCheck = useCallback(
    async (targetPath, { onBlocked } = {}) => {
      const path = targetPath || "/home";

      if (isRestrictedMenuDisabled(status, path)) {
        onBlocked?.({ status, reason: "disabled" });
        return false;
      }

      const result = await refreshSubscriptionValidity();
      const nextStatus = result?.status ?? STATUS_ERROR;

      if (nextStatus === STATUS_NOT_PURCHASED && path === "/home") {
        setShowSubscribeModal(true);
      }

      if (!canAccessPathWithSubscription(nextStatus, path)) {
        const redirectTo = getSubscriptionRedirectForPath(nextStatus, path) || "/subscription";
        onBlocked?.({ status: nextStatus, reason: "redirect", redirectTo });
        if (nextStatus === STATUS_NOT_PURCHASED) {
          setShowSubscribeModal(true);
        }
        navigate(redirectTo);
        return false;
      }

      navigate(path);
      return true;
    },
    [status, refreshSubscriptionValidity, navigate]
  );

  const value = useMemo(
    () => ({
      status,
      checking,
      expirationDate,
      validityError,
      showSubscribeModal,
      dismissSubscribeModal,
      openSubscribeModal,
      refreshSubscriptionValidity,
      navigateWithSubscriptionCheck,
      isRestrictedMenuDisabled: (pathname) => isRestrictedMenuDisabled(status, pathname),
      canAccessPath: (pathname) => canAccessPathWithSubscription(status, pathname),
      isRestrictedPath: isSubscriptionRestrictedPath
    }),
    [
      status,
      checking,
      expirationDate,
      validityError,
      showSubscribeModal,
      dismissSubscribeModal,
      openSubscribeModal,
      refreshSubscriptionValidity,
      navigateWithSubscriptionCheck
    ]
  );

  return (
    <SubscriptionAccessContext.Provider value={value}>
      {children}
    </SubscriptionAccessContext.Provider>
  );
};

SubscriptionAccessProvider.propTypes = {
  children: PropTypes.node.isRequired
};

const useSubscriptionAccess = () => useContext(SubscriptionAccessContext);

export {
  SubscriptionAccessProvider,
  SubscriptionAccessContext,
  useSubscriptionAccess
};
